import { useCallback, useState } from 'react';
import { isEmpty, isNil } from 'lodash';
import { AllocationName, MarketAdjustmentWritable } from 'api';
import { NewVersionFormState } from 'pages/Allocation/allocations/components/NewVersionForm/NewVersionForm.props';
import { DEFAULT_VERSION_FORM_STATE } from 'pages/ValuationsAllocation/common/constants/valuations';
import { ValuationWithReferences } from 'pages/ValuationsAllocation/types';
import { useCreateNewVersion } from 'services/hooks/valuations';
import { dbShortDate, getArrayValue, getObjectValue, getStringValue } from 'utilities';
import {
  UseFundInvestmentAllocationVersionParams,
  UseFundInvestmentAllocationVersionReturn,
} from './useFundInvestmentAllocationVersion.types';

const useFundInvestmentAllocationVersion = (
  params: UseFundInvestmentAllocationVersionParams
): UseFundInvestmentAllocationVersionReturn => {
  const {
    allocationVersionDialogProps,
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    currentMeasurementDate,
    currentMeasurementDateId,
    marketCurrency,
    marketDate,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  } = params;
  // Allocation version dialog
  const {
    getVersions: getAllocationVersions,
    onClose: onCloseAllocationVersionDialog,
    open: isAllocationVersionDialogOpen,
    versions: allocationVersions,
  } = getObjectValue(allocationVersionDialogProps);

  // Create New Version
  const { mutateAsync: saveNewAllocationVersion } = useCreateNewVersion();

  // New allocation version
  const [newAllocationVersionFormState, setNewAllocationVersionFormState] =
    useState<NewVersionFormState>(DEFAULT_VERSION_FORM_STATE);
  const [isOpenNewAllocationVersionDialog, setIsOpenNewAllocationVersionDialog] = useState<boolean>(false);

  const showNewAllocationVersionDialog = useCallback(() => setIsOpenNewAllocationVersionDialog(true), []);

  const closeNewAllocationVersionDialog = useCallback(() => {
    setIsOpenNewAllocationVersionDialog(false);
    setNewAllocationVersionFormState(DEFAULT_VERSION_FORM_STATE);
  }, []);

  // Create New Valuations Allocation Version
  const createNewAllocationVersion = useCallback(async () => {
    if (!isEmpty(valuationByAllocation) && !isEmpty(currentAllocation)) {
      const hasInvalidCells = !isNil(areAllCellsValid) && !areAllCellsValid();

      setHasValidationErrors(hasInvalidCells);

      // If there are invalid cells or funds, do not proceed with saving
      if (hasInvalidCells) {
        setIsValidating(false);
        return;
      }

      // If all Cells are valid
      const { values: newAllocationVersionValues } = newAllocationVersionFormState;
      const { name: newAllocationVersionName } = getObjectValue(newAllocationVersionValues);

      const marketAdjustmentIsNotAvailable =
        isNil(currentAsOfDate) && isNil(currentMarketComparisons) && isNil(currentMarketAdjustment);

      let updatedMarketAdjustments: MarketAdjustmentWritable[] = isEmpty(currentDeletedMarketAdjustments)
        ? [
          {
            ...(currentMarketAdjustment as MarketAdjustmentWritable),
            as_of_date: getStringValue(currentAsOfDate),
            market_comparisons: getArrayValue(currentMarketComparisons),
            deleted_market_comparisons: getArrayValue(currentDeletedMarketComparisons),
          },
        ]
        : []; // If there are deleted market adjustments, do not include the current market adjustment

      if (marketAdjustmentIsNotAvailable) {
        updatedMarketAdjustments = getArrayValue(valuationByAllocation.market_adjustments);
      }

      const valuationToBeDuplicated = {
        ...valuationByAllocation,
        name: getStringValue(newAllocationVersionName),
        allocation: {
          ...currentAllocation,
          is_final: false,
          name: getStringValue(newAllocationVersionName),
        },
        deleted_approaches: [],
        deleted_market_adjustments: getArrayValue(currentDeletedMarketAdjustments),
        market_adjustments: updatedMarketAdjustments,
        market_currency: marketCurrency,
        market_date: dbShortDate(marketDate),
      } as ValuationWithReferences;

      const { valuation: newValuationVersion } = await saveNewAllocationVersion({
        valuation: valuationToBeDuplicated,
      });
      const { allocation: newAllocationVersion } = getObjectValue(newValuationVersion);

      if (!isEmpty(newAllocationVersion)) {
        const { id: newValuationId } = getObjectValue(newValuationVersion);

        const {
          id: newAllocationId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          version,
        } = getObjectValue(newAllocationVersion);

        const createdAllocationVersion = {
          id: newAllocationId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          valuation: newValuationId,
          version,
        } as AllocationName;

        getAllocationVersions?.(createdAllocationVersion);
      }

      // Mark changes as not pending
      cleanUnsavedChanges();
      setArePendingChanges(false);
    }
  }, [
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentAsOfDate,
    currentDeletedMarketAdjustments,
    currentDeletedMarketComparisons,
    currentMarketAdjustment,
    currentMarketComparisons,
    getAllocationVersions,
    marketCurrency,
    marketDate,
    newAllocationVersionFormState,
    saveNewAllocationVersion,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  ]);

  const createNewBlankAllocationVersion = useCallback(async () => {
    if (!isEmpty(valuationByAllocation) && !isEmpty(currentAllocation)) {
      const hasInvalidCells = !isNil(areAllCellsValid) && !areAllCellsValid();

      setHasValidationErrors(hasInvalidCells);

      // If there are invalid cells or funds, do not proceed with saving
      if (hasInvalidCells) {
        setIsValidating(false);
        return;
      }

      // Version number and ownership will be set in by the backend API
      const blankValuationAllocation = {
        allocation_id: currentAllocation.id,
        allocation: {
          allocation_scenarios: [],
          company_measurement_date: currentMeasurementDateId,
          date: currentMeasurementDate,
          fund_conclusion: [],
          id: currentAllocation.id,
          is_final: false,
          is_published: false,
          name: `Firm version - ${currentMeasurementDate}`,
        },
        deleted_approaches: [],
        deleted_market_adjustments: [],
        id: valuationByAllocation.id,
        market_adjustments: [],
        market_currency: marketCurrency,
        name: `Firm version - ${currentMeasurementDate}`,
        valuations_approaches: [],
        weighting_probabilities: [],
      } as unknown as ValuationWithReferences;

      const { valuation: blankValuationVersion } = await saveNewAllocationVersion({
        valuation: blankValuationAllocation,
      });
      const { allocation: blankAllocationVersion } = getObjectValue(blankValuationVersion);

      if (!isEmpty(blankAllocationVersion)) {
        const { id: blankValuationVersionId } = getObjectValue(blankValuationVersion);

        const {
          id: blankAllocationVersionId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          version,
        } = getObjectValue(blankAllocationVersion);

        const createdAllocationVersion = {
          id: blankAllocationVersionId,
          is_final: isFinal,
          is_published: isPublished,
          name,
          slug,
          valuation: blankValuationVersionId,
          version,
        } as AllocationName;

        getAllocationVersions?.(createdAllocationVersion);
      }

      // Mark changes as not pending
      cleanUnsavedChanges();
      setArePendingChanges(false);
    }
  }, [
    areAllCellsValid,
    cleanUnsavedChanges,
    currentAllocation,
    currentMeasurementDate,
    currentMeasurementDateId,
    getAllocationVersions,
    marketCurrency,
    saveNewAllocationVersion,
    setArePendingChanges,
    setHasValidationErrors,
    setIsValidating,
    valuationByAllocation,
  ]);

  return {
    allocationVersions,
    closeNewAllocationVersionDialog,
    createNewAllocationVersion,
    createNewBlankAllocationVersion,
    getAllocationVersions,
    isAllocationVersionDialogOpen,
    isOpenNewAllocationVersionDialog,
    onCloseAllocationVersionDialog,
    setNewAllocationVersionFormState,
    showNewAllocationVersionDialog,
  };
};

export default useFundInvestmentAllocationVersion;
